import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Mail,
  Building2,
  Calendar,
  AlertTriangle,
  MessageSquare,
  Clock,
  CheckCircle2,
  Activity,
  Brain,
  Sparkles,
  RefreshCw,
  Copy,
  Check,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Employee } from '../data/employees';

const EmployeeProfile: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { employees } = useApp();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [playbook, setPlaybook] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const found = employees.find((e) => e.id === id);
    setEmployee(found || null);
  }, [id, employees]);

  const generatePlaybook = async () => {
    if (!employee) return;
    setIsGenerating(true);

    // Simulate AI playbook generation
    await new Promise((resolve) => setTimeout(resolve, 2000));

    const playbooks = {
      High: `## Immediate Action Required

### What to Do Today
1. **Schedule a private 1:1** - Reach out to ${employee.name} within the next 24 hours. Choose a comfortable, informal setting.
2. **Prepare with empathy** - Review recent project assignments and any feedback received. Approach without assumptions.
3. **Open the conversation gently** - Start with appreciation for their work before exploring how they're feeling.

### What to Do This Week
1. **Deep dive on workload** - Review task allocation and identify any overwhelming patterns. Consider temporary load balancing.
2. **Connect with skip-level** - Brief HR or your manager about concerns while respecting privacy boundaries.
3. **Set up regular check-ins** - Propose weekly 15-minute sync sessions to maintain open communication.
4. **Identify quick wins** - Find opportunities to celebrate small achievements and rebuild engagement.

### How to Communicate
- Use phrases like: "I've noticed you seem stretched thin lately, and I want to support you."
- Avoid: Accusatory language or mentioning "performance concerns" directly.
- Listen more than you speak (80/20 rule).
- End with concrete next steps and follow through on commitments.

### Red Flags to Watch
${employee.behavioralFlags.map((f) => `- ${f}`).join('\n')}

### Expected Outcome
With proper intervention, we expect engagement indicators to stabilize within 2-3 weeks. Continue monitoring and adjust approach based on response.`,

      Medium: `## Proactive Engagement Plan

### What to Do Today
1. **Informal check-in** - Drop by ${employee.name}'s workspace or send a casual message checking in.
2. **Recognize recent contributions** - Find something specific to acknowledge in their recent work.

### What to Do This Week
1. **Career conversation** - Schedule time to discuss their growth trajectory and interests.
2. **Development opportunity** - Identify a stretch project or learning opportunity aligned with their interests.
3. **Team connection** - Ensure they're included in upcoming team activities and feel part of the group.

### How to Communicate
- Lead with curiosity: "What's been energizing you lately? What's been draining?"
- Show investment in their growth: "I've been thinking about opportunities that might interest you..."
- Be transparent about why you're checking in without being alarmist.

### Monitoring Plan
Continue weekly observational check-ins. If engagement continues to decline over the next 2 weeks, escalate to high-risk protocol.`,

      Low: `## Engagement Maintenance Plan

### What to Do Today
1. **Express appreciation** - ${employee.name} shows healthy engagement patterns. Reinforce this with specific recognition.

### What to Do This Week
1. **Growth conversation** - Discuss future aspirations and how you can support their development.
2. **Peer mentorship** - Consider pairing them with newer team members to leverage their positive energy.
3. **Visibility opportunities** - Look for ways to showcase their work to leadership.

### How to Communicate
- Celebrate: "Your consistent contribution to the team doesn't go unnoticed."
- Explore: "Where do you see yourself in the next year? How can I help you get there?"

### Retention Strategy
Engaged employees are your culture carriers. Invest in their growth to maintain momentum and prevent future disengagement.`,
    };

    setPlaybook(playbooks[employee.riskLevel]);
    setIsGenerating(false);
  };

  const copyToClipboard = async () => {
    if (playbook) {
      await navigator.clipboard.writeText(playbook);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!employee) {
    return (
      <div style={styles.loadingContainer}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <Sparkles size={40} color="#06BCCA" />
        </motion.div>
        <p>Loading employee profile...</p>
      </div>
    );
  }

  const getRiskBadgeStyle = (level: string) => {
    switch (level) {
      case 'High':
        return { background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' };
      case 'Medium':
        return { background: '#FEF3C7', color: '#D97706', border: '1px solid #FDE68A' };
      case 'Low':
        return { background: '#D1FAE5', color: '#059669', border: '1px solid #A7F3D0' };
      default:
        return {};
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <motion.header
        style={styles.header}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={20} />
          Back to Dashboard
        </button>
      </motion.header>

      <div style={styles.content}>
        {/* Profile Card */}
        <motion.div
          style={styles.profileCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div style={styles.profileHeader}>
            <div style={styles.avatarLarge}>
              {employee.name.charAt(0)}
            </div>
            <div style={styles.profileInfo}>
              <h1 style={styles.employeeName}>{employee.name}</h1>
              <p style={styles.employeeRole}>{employee.role}</p>
              <div style={styles.profileMeta}>
                <span style={styles.metaItem}>
                  <Mail size={14} />
                  {employee.email}
                </span>
                <span style={styles.metaItem}>
                  <Building2 size={14} />
                  {employee.department}
                </span>
                <span style={styles.metaItem}>
                  <Calendar size={14} />
                  Joined {new Date(employee.startDate).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div style={styles.riskScoreCard}>
              <div style={styles.riskScoreValue}>{employee.riskScore}</div>
              <div style={styles.riskScoreLabel}>DDI</div>
              <span style={{ ...styles.riskBadge, ...getRiskBadgeStyle(employee.riskLevel) }}>
                {employee.riskLevel} Risk
              </span>
            </div>
          </div>
        </motion.div>

        <div style={styles.grid}>
          {/* Left Column */}
          <div style={styles.leftColumn}>
            {/* Risk Explanation */}
            <motion.div
              style={styles.card}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div style={styles.cardHeader}>
                <Brain size={20} color="#69277F" />
                <h3 style={styles.cardTitle}>Why This DDI?</h3>
              </div>
              <p style={styles.riskExplanation}>{employee.riskExplanation}</p>
            </motion.div>

            {/* Behavioral Flags */}
            <motion.div
              style={styles.card}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div style={styles.cardHeader}>
                <AlertTriangle size={20} color="#F59E0B" />
                <h3 style={styles.cardTitle}>Behavioral Red Flags</h3>
              </div>
              {employee.behavioralFlags.length > 0 ? (
                <div style={styles.flagsList}>
                  {employee.behavioralFlags.map((flag, index) => (
                    <motion.div
                      key={flag}
                      style={styles.flagItem}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                    >
                      <div style={styles.flagDot} />
                      {flag}
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p style={styles.noFlags}>No behavioral flags detected</p>
              )}
            </motion.div>

            {/* Recent Activity */}
            <motion.div
              style={styles.card}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div style={styles.cardHeader}>
                <Activity size={20} color="#06BCCA" />
                <h3 style={styles.cardTitle}>Recent Activity Metrics</h3>
              </div>
              <div style={styles.activityGrid}>
                <div style={styles.activityItem}>
                  <MessageSquare size={18} color="#6B7280" />
                  <span style={styles.activityValue}>
                    {employee.recentActivity.meetingsAttended}
                  </span>
                  <span style={styles.activityLabel}>Meetings Attended</span>
                </div>
                <div style={styles.activityItem}>
                  <Clock size={18} color="#6B7280" />
                  <span style={styles.activityValue}>
                    {employee.recentActivity.messagesResponse}h
                  </span>
                  <span style={styles.activityLabel}>Avg Response Time</span>
                </div>
                <div style={styles.activityItem}>
                  <CheckCircle2 size={18} color="#6B7280" />
                  <span style={styles.activityValue}>
                    {employee.recentActivity.tasksCompleted}
                  </span>
                  <span style={styles.activityLabel}>Tasks Completed</span>
                </div>
                <div style={styles.activityItem}>
                  <Activity size={18} color="#6B7280" />
                  <span style={styles.activityValue}>
                    {employee.recentActivity.overtimeHours}h
                  </span>
                  <span style={styles.activityLabel}>Overtime Hours</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Playbook */}
          <div style={styles.rightColumn}>
            <motion.div
              style={styles.playbookCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div style={styles.playbookHeader}>
                <div style={styles.playbookIcon}>
                  <Sparkles size={24} color="#FF68C5" />
                </div>
                <div>
                  <h3 style={styles.playbookTitle}>AI Manager Playbook</h3>
                  <p style={styles.playbookSubtitle}>
                    Personalized action plan generated by Dynami AI
                  </p>
                </div>
              </div>

              {!playbook && !isGenerating && (
                <motion.button
                  style={styles.generateButton}
                  onClick={generatePlaybook}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Brain size={20} />
                  Generate Action Playbook
                </motion.button>
              )}

              <AnimatePresence>
                {isGenerating && (
                  <motion.div
                    style={styles.generatingState}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    >
                      <Sparkles size={32} color="#06BCCA" />
                    </motion.div>
                    <p style={styles.generatingText}>
                      Analyzing engagement patterns and generating personalized recommendations...
                    </p>
                    <div style={styles.progressDots}>
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={i}
                          style={styles.dot}
                          animate={{ scale: [1, 1.3, 1] }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            delay: i * 0.2,
                          }}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}

                {playbook && !isGenerating && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div style={styles.playbookActions}>
                      <button
                        style={styles.actionButton}
                        onClick={copyToClipboard}
                      >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                      <button
                        style={styles.actionButton}
                        onClick={generatePlaybook}
                      >
                        <RefreshCw size={16} />
                        Regenerate
                      </button>
                    </div>
                    <div style={styles.playbookContent}>
                      {playbook.split('\n').map((line, i) => {
                        if (line.startsWith('## ')) {
                          return (
                            <h2 key={i} style={styles.playbookH2}>
                              {line.replace('## ', '')}
                            </h2>
                          );
                        }
                        if (line.startsWith('### ')) {
                          return (
                            <h3 key={i} style={styles.playbookH3}>
                              {line.replace('### ', '')}
                            </h3>
                          );
                        }
                        if (line.startsWith('- ')) {
                          return (
                            <div key={i} style={styles.playbookListItem}>
                              <div style={styles.playbookBullet} />
                              {line.replace('- ', '')}
                            </div>
                          );
                        }
                        if (line.match(/^\d+\./)) {
                          return (
                            <div key={i} style={styles.playbookOrderedItem}>
                              {line}
                            </div>
                          );
                        }
                        if (line.trim()) {
                          return <p key={i} style={styles.playbookP}>{line}</p>;
                        }
                        return <br key={i} />;
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    background: '#F8F9FC',
  },
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    color: '#69277F',
  },
  header: {
    padding: '20px 32px',
    background: 'white',
    borderBottom: '1px solid #E5E7EB',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    background: 'transparent',
    border: '2px solid #E5E7EB',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  content: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
  },
  profileCard: {
    background: 'white',
    borderRadius: '20px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  profileHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '24px',
    flexWrap: 'wrap',
  },
  avatarLarge: {
    width: '80px',
    height: '80px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #69277F 0%, #FF68C5 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '32px',
    fontWeight: 700,
  },
  profileInfo: {
    flex: 1,
    minWidth: '250px',
  },
  employeeName: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    margin: '0 0 4px 0',
  },
  employeeRole: {
    fontSize: '16px',
    color: '#6B7280',
    marginBottom: '12px',
  },
  profileMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '14px',
    color: '#6B7280',
  },
  riskScoreCard: {
    textAlign: 'center',
    padding: '20px 32px',
    background: '#F9FAFB',
    borderRadius: '16px',
  },
  riskScoreValue: {
    fontSize: '48px',
    fontWeight: 700,
    color: '#1F2937',
    fontFamily: 'var(--font-heading)',
  },
  riskScoreLabel: {
    fontSize: '13px',
    color: '#6B7280',
    marginBottom: '12px',
  },
  riskBadge: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: 600,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  riskExplanation: {
    fontSize: '14px',
    lineHeight: 1.7,
    color: '#4B5563',
    margin: 0,
  },
  flagsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  flagItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: '#374151',
    padding: '10px 14px',
    background: '#FEF3C7',
    borderRadius: '10px',
  },
  flagDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#F59E0B',
  },
  noFlags: {
    fontSize: '14px',
    color: '#10B981',
    fontStyle: 'italic',
    margin: 0,
  },
  activityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  activityItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
    textAlign: 'center',
  },
  activityValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1F2937',
  },
  activityLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  playbookCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    position: 'sticky',
    top: '24px',
  },
  playbookHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '20px',
  },
  playbookIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, rgba(255, 104, 197, 0.1) 0%, rgba(255, 200, 0, 0.1) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playbookTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '0 0 4px 0',
  },
  playbookSubtitle: {
    fontSize: '13px',
    color: '#6B7280',
    margin: 0,
  },
  generateButton: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    color: 'white',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    cursor: 'pointer',
    border: 'none',
  },
  generatingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  generatingText: {
    fontSize: '14px',
    color: '#6B7280',
    marginTop: '16px',
    lineHeight: 1.5,
  },
  progressDots: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#06BCCA',
  },
  playbookActions: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    background: '#F3F4F6',
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#4B5563',
    cursor: 'pointer',
  },
  playbookContent: {
    maxHeight: '500px',
    overflowY: 'auto',
    paddingRight: '8px',
  },
  playbookH2: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#69277F',
    margin: '0 0 16px 0',
    paddingBottom: '8px',
    borderBottom: '2px solid #E5E7EB',
  },
  playbookH3: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1F2937',
    margin: '20px 0 12px 0',
  },
  playbookP: {
    fontSize: '14px',
    color: '#4B5563',
    lineHeight: 1.6,
    margin: '8px 0',
  },
  playbookListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    fontSize: '14px',
    color: '#4B5563',
    lineHeight: 1.6,
    marginLeft: '8px',
    marginBottom: '6px',
  },
  playbookBullet: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#69277F',
    marginTop: '8px',
    flexShrink: 0,
  },
  playbookOrderedItem: {
    fontSize: '14px',
    color: '#4B5563',
    lineHeight: 1.6,
    marginLeft: '8px',
    marginBottom: '6px',
  },
};

export default EmployeeProfile;

